// src/pages/About.js (or wherever your About component resides)
import React from 'react';
import {
  Container,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';

import TeamCarousel from '../components/TeamCarasoul'; // Adjust path as needed
import type { TeamMember } from '../components/TeamMemberCard'; // Adjust path
import InteractiveHorizontalCarousel from '../components/InteractiveHorizontalImageCarousel';
import { useAppLanguage } from '../hooks/useAppLanguage'; // NEW: Import the language hook

// --- Images for the Interactive Carousel ---
const carouselOfficeImages: string[] = [
  '/img/clinic/building_new.jpg',
  '/img/clinic/outdoor_new.jpg',
  '/img/clinic/front_office_new.jpg',
  'img/clinic/waiting_area_new.jpg',
  '/img/clinic/cleaning_station_1_new.jpg',
  '/img/clinic/cleaning_station_2_new.jpg',
  'img/clinic/op1_new.jpg',
  '/img/clinic/op3_new.jpg',
  '/img/clinic/op4_new.jpg',
  '/img/clinic/cbct_overview_new.jpg',
  '/img/clinic/cbct.jpg',
  '/img/clinic/itero.jpg',
];

// --- Base Team Member Data (non-translatable parts) ---
const baseTeamMembers = [
  {
    id: 'ivy_yang',
    image: '/img/ivy-photo.jpg',
  },
  {
    id: 'yingying_chi',
    image: '/img/chi-photo.png'
  },
  {
    name: 'Umi',
    id: 'umi',
    image: '/img/umi.jpg',
  }
  // Add other team members' base data here
];

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useAppLanguage(); // NEW: Initialize the language hook

  // NEW: Create translated team members data
  const translatedTeamMembers: TeamMember[] = baseTeamMembers.map(member => ({
    ...member, // Keep id, name, image
    // Fetch translatable fields using the 't' function
    // Keys should match your translation JSON files, e.g., teamMembersData.ivy_yang.role
    name: t(`teamMembersData.${member.id}.name`, 'Default Name'),
    role: t(`teamMembersData.${member.id}.role`, 'Default Role'),
    language: t(`teamMembersData.${member.id}.language`, 'Default Language'),
    description: t(`teamMembersData.${member.id}.description`, 'Default description.'),
  }));

  return (
    <Box
      id="about"
      sx={{
        py: { xs: 6, md: 8 },
        background: 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(26,35,126,0.04) 0%, rgba(26,35,126,0) 70%)',
          zIndex: 0,
        }
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant={isMobile ? "h4" : "h2"}
            component="h1"
            align="left"
            gutterBottom
            sx={{ 
              mb: { xs: 4, md: 6 },
              px: { xs: 2, md: 0 },
              fontWeight: 700,
              color: 'text.secondary'
            }}
          >
            {/* NEW: Translate page title */}
            {t('about.pageTitle', 'Meet Our Team')}
          </Typography>
        </motion.div>

        <Box sx={{ mb: 6, px: { xs: 2, md: 0 } }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 3
            }}
          >
            {/* NEW: Translate section title */}
            {t('about.multilingualCare.title', 'Multilingual Dental Care in Cupertino')}
          </Typography>
          <Typography
            variant="body1"
            paragraph
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.7,
              color: 'text.secondary'
            }}
          >
            {/* NEW: Translate section description */}
            {t('about.multilingualCare.description', "At Pacifica Dental, we're proud to offer comprehensive dental care in both English and Chinese. Our Chinese-speaking dentists understand the importance of clear communication in dental care and are committed to providing a comfortable experience for our Chinese-speaking patients. Located in the heart of Cupertino, we're your trusted local dental practice for the entire family.")}
          </Typography>
        </Box>

        {/* Pass the translatedTeamMembers data as a prop */}
        <TeamCarousel teamMembers={translatedTeamMembers} />

        <Box sx={{ my: { xs: 5, md: 8 } }}>
          <Divider sx={{ mb: { xs: 4, md: 6 } }} />
          <motion.div initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <Typography
              variant={isMobile ? "h5" : "h3"}
              component="h2"
              align="center"
              gutterBottom
              sx={{
                mb: { xs: 3, md: 5 },
                fontWeight: 600,
                color: 'text.secondary'
              }}
            >
              {/* NEW: Translate section title */}
              {t('about.glimpse.title', 'A Glimpse Into Our Clinic')}
            </Typography>
          </motion.div>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <InteractiveHorizontalCarousel
            images={carouselOfficeImages}
            imageWidth={isMobile ? 280 : 300} // Example: larger images on desktop
            imageHeight={isMobile ? 250 : 700} // Example: larger images on desktop
            // gap={isMobile ? 12 : 16}
          />
        </motion.div>
      </Container>
    </Box>
  );
};

export default About;
